import React from "react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";
import { useTranslation } from "react-i18next";
import type {
  ReportBlock,
  MetricData,
  ChartData,
  TableData,
  TextData,
  NarrativeData,
} from "../reporting.service";
import { BLOCK_META } from "./blockMeta";

const PALETTE = [
  "#5540af",
  "#3b82f6",
  "#a78bfa",
  "#06b6d4",
  "#f59e0b",
  "#10b981",
  "#ef4444",
  "#ec4899",
];

const isEmpty = (v: any): boolean =>
  v == null ||
  (Array.isArray(v) && v.length === 0) ||
  (typeof v === "object" && Object.keys(v).length === 0);

const fmtNumber = (n: number | null | undefined): string => {
  if (n == null) return "—";
  if (Math.abs(n) >= 1000) {
    return Intl.NumberFormat("en", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(n);
  }
  return String(n);
};

// ── Per-type content ──────────────────────────────────────────────────────────

const TextContent: React.FC<{ block: ReportBlock }> = ({ block }) => {
  const data = block.data as TextData | undefined;
  const body = data?.body ?? block.config.body ?? "";
  if (!body)
    return <p className="rb-block__helper">{BLOCK_META.text.helper}</p>;
  return <p className="rb-block__text">{body}</p>;
};

const MetricContent: React.FC<{ block: ReportBlock; sourceLabel?: string | null }> = ({
  block,
  sourceLabel,
}) => {
  const { t } = useTranslation("reporting");
  const data = block.data as MetricData | undefined;
  if (!data || isEmpty(data.items))
    return <p className="rb-block__helper">{BLOCK_META.metric.helper}</p>;
  return (
    <>
      {sourceLabel && (
        <div className="rb-block__source-tag">{t("blockRenderer.sourceTag", { source: sourceLabel })}</div>
      )}
      <div className="rb-metric-row">
        {data.items.map((it, i) => {
          const valueStr =
            it.unit === "%"
              ? `${fmtNumber(it.value)}%`
              : fmtNumber(it.value);
          return (
            <div className="rb-metric" key={i}>
              <div className="rb-metric__value">{valueStr}</div>
              <div className="rb-metric__label">{it.label}</div>
              {it.delta != null && (
                <div
                  className={`rb-metric__delta rb-metric__delta--${
                    it.direction ?? "flat"
                  }`}
                >
                  {it.direction === "down" ? "↘" : it.direction === "up" ? "↗" : "→"}{" "}
                  {Math.abs(it.delta)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

const ChartContent: React.FC<{ block: ReportBlock; sourceLabel?: string | null }> = ({
  block,
  sourceLabel,
}) => {
  const { t } = useTranslation("reporting");
  const data = block.data as ChartData | undefined;
  if (!data || isEmpty(data.labels))
    return <p className="rb-block__helper">{BLOCK_META.chart.helper}</p>;

  const type = data.chart_type ?? block.config.chart_type ?? "bar";
  const series = data.series ?? [];

  return (
    <>
      {sourceLabel && (
        <div className="rb-block__source-tag">{t("blockRenderer.sourceTag", { source: sourceLabel })}</div>
      )}
      <div className="rb-chart">
        <ResponsiveContainer width="100%" height={240}>
          {type === "pie" || type === "donut" ? (
            <PieChart>
              <Pie
                data={data.labels.map((l, i) => ({
                  name: l,
                  value: series[0]?.values?.[i] ?? 0,
                }))}
                dataKey="value"
                nameKey="name"
                cx="45%"
                cy="50%"
                innerRadius={type === "donut" ? 55 : 0}
                outerRadius={85}
                label={({ percent }) =>
                  percent ? `${Math.round(percent * 100)}%` : ""
                }
              >
                {data.labels.map((_, i) => (
                  <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                ))}
              </Pie>
              <Legend layout="vertical" align="right" verticalAlign="middle" />
              <Tooltip />
            </PieChart>
          ) : type === "line" ? (
            <LineChart
              data={data.labels.map((l, i) => {
                const row: Record<string, any> = { name: l };
                series.forEach((s) => (row[s.name] = s.values?.[i] ?? 0));
                return row;
              })}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              {series.map((s, i) => (
                <Line
                  key={s.name}
                  type="monotone"
                  dataKey={s.name}
                  stroke={PALETTE[i % PALETTE.length]}
                />
              ))}
            </LineChart>
          ) : (
            <BarChart
              data={data.labels.map((l, i) => {
                const row: Record<string, any> = { name: l };
                series.forEach((s) => (row[s.name] = s.values?.[i] ?? 0));
                return row;
              })}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              {series.map((s, i) => (
                <Bar key={s.name} dataKey={s.name} fill={PALETTE[i % PALETTE.length]} />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
        <ul className="rb-chart-values">
          {data.labels.map((label, i) => (
            <li key={label}>
              <span className="rb-cv-label">{label}</span>
              <span className="rb-cv-val">
                {series
                  .map(
                    (s) =>
                      `${series.length > 1 ? s.name + ": " : ""}${
                        s.values?.[i] ?? 0
                      }`,
                  )
                  .join("  ·  ")}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
};

const TableContent: React.FC<{ block: ReportBlock; sourceLabel?: string | null }> = ({
  block,
  sourceLabel,
}) => {
  const { t } = useTranslation("reporting");
  const data = block.data as TableData | undefined;
  if (!data || isEmpty(data.rows))
    return <p className="rb-block__helper">{BLOCK_META.table.helper}</p>;
  return (
    <>
      {sourceLabel && (
        <div className="rb-block__source-tag">{t("blockRenderer.sourceTag", { source: sourceLabel })}</div>
      )}
      <table className="rb-table">
        <thead>
          <tr>
            {data.columns.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

const NarrativeContent: React.FC<{ block: ReportBlock }> = ({ block }) => {
  const { t } = useTranslation("reporting");
  const data = block.data as NarrativeData | undefined;
  if (!data || isEmpty(data.paragraphs))
    return (
      <p className="rb-block__helper rb-block__helper--ai">
        {BLOCK_META.ai_narrative.helper}
      </p>
    );
  return (
    <div className="rb-narrative">
      {data.source === "fallback" && (
        <span className="rb-narrative__hint">{t("blockRenderer.autoSummary")}</span>
      )}
      {data.paragraphs.map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </div>
  );
};

const Content: React.FC<{ block: ReportBlock; sourceLabel?: string | null }> = ({
  block,
  sourceLabel,
}) => {
  switch (block.type) {
    case "text":
      return <TextContent block={block} />;
    case "metric":
      return <MetricContent block={block} sourceLabel={sourceLabel} />;
    case "chart":
      return <ChartContent block={block} sourceLabel={sourceLabel} />;
    case "table":
      return <TableContent block={block} sourceLabel={sourceLabel} />;
    case "ai_narrative":
      return <NarrativeContent block={block} />;
    default:
      return null;
  }
};

// ── Wrapper ───────────────────────────────────────────────────────────────────

interface Props {
  block: ReportBlock;
  mode?: "builder" | "preview";
  selected?: boolean;
  sourceLabel?: string | null;
  onSelect?: () => void;
}

const BlockRenderer: React.FC<Props> = ({
  block,
  mode = "builder",
  selected = false,
  sourceLabel,
  onSelect,
}) => {
  const meta = BLOCK_META[block.type];

  if (mode === "preview") {
    return (
      <div className="rb-block rb-block--preview">
        <h3 className="rb-block__title">{block.title}</h3>
        <Content block={block} sourceLabel={sourceLabel} />
      </div>
    );
  }

  return (
    <div
      className={`rb-block ${selected ? "rb-block--selected" : ""}`}
      onClick={onSelect}
      role="button"
      tabIndex={0}
    >
      <div className="rb-block__chip" style={{ color: meta.accent }}>
        {meta.label}
        <span className="rb-block__handle">⋮⋮</span>
      </div>
      <h3 className="rb-block__title">{block.title}</h3>
      <Content block={block} sourceLabel={sourceLabel} />
    </div>
  );
};

export default BlockRenderer;
